// Deployment-related types — deploy modes, infrastructure targets, cloud
// connections, and resolved build/deploy strategies.

import type { CloudProvider } from "./stack";

/**
 * Deploy mode — controls who builds and who deploys.
 * Exactly two ownership models are supported (see contracts/pipeline-architecture.yaml).
 */
export enum PipelineMode {
  RUSHA_MANAGED = "rusha_managed",
  CI_BUILD_RUSHA_DEPLOY = "ci_build_rusha_deploy",
}

// ── Infrastructure Target types ────────────────────────────────────────────────

export type InfraTargetStatus =
  "pending" | "active" | "failed" | "disconnected";

export interface InfrastructureTarget {
  id: number;
  name: string;
  cloudProvider: CloudProvider;
  region: string;
  status: InfraTargetStatus;
  organizationId: number;
  cloudConnectionId?: number | null;
  // K8s fields
  clusterName?: string;
  namespace?: string;
  clusterApiUrl?: string;
  // Agent fields
  agentTokenHash?: string;
  agentLastSeen?: string;
  agentVersion?: string;
  // Linux fields
  hostname?: string;
  // Rusha-hosted
  rushaNamespace?: string;
  // Registry configuration
  containerRegistry?: string;
  artifactRegistry?: string;
  containerRegistryType?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateInfrastructureTargetRequest {
  name: string;
  cloudProvider: CloudProvider;
  region: string;
  organizationId: number;
  cloudConnectionId?: number;
  clusterName?: string;
  namespace?: string;
  clusterApiUrl?: string;
  hostname?: string;
  rushaNamespace?: string;
  // Registry configuration
  containerRegistry?: string;
  artifactRegistry?: string;
  containerRegistryType?: string;
}

export interface UpdateInfrastructureTargetRequest {
  name?: string;
  region?: string;
  status?: InfraTargetStatus;
  clusterName?: string;
  namespace?: string;
  clusterApiUrl?: string;
  hostname?: string;
  rushaNamespace?: string;
  cloudConnectionId?: number | null;
  // Registry configuration
  containerRegistry?: string;
  artifactRegistry?: string;
  containerRegistryType?: string;
}

// ── Cloud Connection types ────────────────────────────────────────────────────

export type CloudConnectionStatus =
  "pending" | "validated" | "active" | "failed";

export interface CloudConnection {
  id: number;
  organizationId: number;
  provider: CloudProvider;
  name: string;
  status: CloudConnectionStatus;
  // AWS fields
  roleArn?: string | null;
  externalId?: string | null;
  accountId?: string | null;
  // GCP fields
  gcpProjectId?: string | null;
  gcpWifProvider?: string | null;
  // Azure fields
  azureSubscriptionId?: string | null;
  azureTenantId?: string | null;
  validatedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  targets?: InfrastructureTarget[];
}

/** Resolved build/deploy strategy info for a service */
export interface ResolvedServiceStrategy {
  serviceName: string;
  serviceType: string;
  buildType: string; // github-actions, static, cloud-build, ecr, none
  deployMethod: string; // minio_cdn, k8s_deployment, aws_cfn, gcp_cloud_run, etc.
  deployConfig?: Record<string, unknown>;
}
